import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { 
  QrCode, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  ArrowLeft,
  Camera,
  Keyboard,
  RotateCcw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScannerPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('camera'); // 'camera' or 'manual'
  const [scanning, setScanning] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    if (html5QrCodeRef.current) {
      stopScanner();
    }

    try {
      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        (decodedText) => {
          handleScan(decodedText);
          stopScanner();
        },
        (errorMessage) => {
          // Ignore scan errors
        }
      );
      setScanning(true);
    } catch (err) {
      console.error("Scanner error:", err);
      setMode('manual');
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        // Ignore stop errors
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const handleScan = async (ticketId) => {
    if (loading) return;
    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(`${API_URL}/api/tickets/validate`, {
        ticket_id: ticketId
      });
      setResult(response.data);
    } catch (error) {
      setResult({
        status: 'ERROR',
        message: 'Network error. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) {
      handleScan(manualInput.trim().toUpperCase());
    }
  };

  const resetScanner = () => {
    setResult(null);
    setManualInput('');
    if (mode === 'camera') {
      startScanner();
    }
  };

  const getResultStyles = () => {
    if (!result) return {};
    
    switch (result.status) {
      case 'VALID':
        return {
          bg: 'bg-[#00C853]',
          icon: CheckCircle,
          title: 'VALID',
          subtitle: 'Ticket validated successfully!'
        };
      case 'ALREADY_USED':
        return {
          bg: 'bg-[#FFB300]',
          icon: AlertTriangle,
          title: 'ALREADY USED',
          subtitle: 'This ticket has been used'
        };
      case 'INVALID':
      case 'ERROR':
      default:
        return {
          bg: 'bg-[#FF3333]',
          icon: XCircle,
          title: 'INVALID',
          subtitle: result.message || 'Ticket not found'
        };
    }
  };

  const resultStyles = getResultStyles();

  return (
    <div className="min-h-screen bg-[#020617] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 scanner-overlay bg-black/80 backdrop-blur-xl">
        <div className="flex items-center justify-between p-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-white hover:bg-white/10"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <h1 className="font-heading text-lg font-bold tracking-tight">Gam Ticket Scanner</h1>
          <div className="w-20"></div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-32 px-4">
        {/* Result Display */}
        {result && (
          <div 
            className={`fixed inset-0 z-40 ${resultStyles.bg} flex flex-col items-center justify-center p-8 ${
              result.status === 'VALID' ? 'scan-success' : 'scan-fail'
            }`}
            data-testid="scan-result"
          >
            <resultStyles.icon className="w-32 h-32 text-white mb-8" />
            <h2 className="font-heading text-5xl font-bold tracking-tight text-white mb-4">
              {resultStyles.title}
            </h2>
            <p className="text-xl text-white/90 mb-4">{resultStyles.subtitle}</p>
            
            {result.ticket && (
              <Card className="bg-white/10 backdrop-blur border-white/20 mt-4 w-full max-w-md">
                <CardContent className="pt-6 text-white">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-white/70 text-sm uppercase tracking-wide">Ticket ID</span>
                      <span className="font-mono font-bold">{result.ticket.ticket_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70 text-sm uppercase tracking-wide">Event</span>
                      <span className="font-semibold">{result.ticket.event_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70 text-sm uppercase tracking-wide">Date</span>
                      <span>{result.ticket.date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70 text-sm uppercase tracking-wide">Venue</span>
                      <span>{result.ticket.venue}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Button
              onClick={resetScanner}
              className="mt-8 bg-white text-black hover:bg-white/90 px-8 py-6 text-lg font-semibold"
              data-testid="scan-another-button"
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Scan Another Ticket
            </Button>
          </div>
        )}

        {/* Mode Toggle */}
        {!result && (
          <div className="flex justify-center gap-2 mb-8">
            <Button
              variant={mode === 'camera' ? 'default' : 'outline'}
              onClick={() => { setMode('camera'); startScanner(); }}
              className={mode === 'camera' ? 'bg-[#0055FF]' : 'bg-transparent border-white/20 text-white'}
              data-testid="camera-mode-button"
            >
              <Camera className="w-4 h-4 mr-2" />
              Camera
            </Button>
            <Button
              variant={mode === 'manual' ? 'default' : 'outline'}
              onClick={() => { setMode('manual'); stopScanner(); }}
              className={mode === 'manual' ? 'bg-[#0055FF]' : 'bg-transparent border-white/20 text-white'}
              data-testid="manual-mode-button"
            >
              <Keyboard className="w-4 h-4 mr-2" />
              Manual
            </Button>
          </div>
        )}

        {/* Camera Scanner */}
        {mode === 'camera' && !result && (
          <div className="flex flex-col items-center">
            <div className="relative w-full max-w-md aspect-square bg-black rounded-lg overflow-hidden">
              <div id="qr-reader" ref={scannerRef} className="w-full h-full"></div>
              
              {/* Viewfinder overlay */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-64 h-64 border-2 border-white/50 rounded-lg scanner-viewfinder"></div>
                </div>
              </div>

              {!scanning && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80">
                  <QrCode className="w-16 h-16 text-white/50 mb-4" />
                  <Button
                    onClick={startScanner}
                    className="bg-[#0055FF] hover:bg-[#0044CC]"
                    data-testid="start-scanner-button"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Start Camera
                  </Button>
                </div>
              )}
            </div>

            <p className="text-white/60 text-center mt-6 text-sm">
              Position the QR code within the frame
            </p>
          </div>
        )}

        {/* Manual Entry */}
        {mode === 'manual' && !result && (
          <div className="max-w-md mx-auto">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="pt-6">
                <form onSubmit={handleManualSubmit} className="space-y-4">
                  <div>
                    <label className="text-xs uppercase tracking-wide text-white/60 font-medium mb-2 block">
                      Enter Ticket ID
                    </label>
                    <Input
                      value={manualInput}
                      onChange={(e) => setManualInput(e.target.value)}
                      placeholder="e.g., EVT-000123"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 h-14 text-lg font-mono text-center"
                      data-testid="manual-ticket-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || !manualInput.trim()}
                    className="w-full h-14 bg-[#0055FF] hover:bg-[#0044CC] text-lg font-semibold"
                    data-testid="validate-ticket-button"
                  >
                    {loading ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Validate Ticket
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Bottom Info */}
      {!result && (
        <footer className="fixed bottom-0 left-0 right-0 scanner-overlay bg-black/80 backdrop-blur-xl p-4">
          <div className="max-w-md mx-auto text-center">
            <p className="text-white/60 text-sm">
              Scan QR codes to validate event tickets
            </p>
          </div>
        </footer>
      )}
    </div>
  );
}
